import React from "react";
import { BadgePercent, BarChart3, Filter, FileText, LifeBuoy, Mail, Siren, Users, BoxIcon } from "lucide-react";
import { PixelCursor } from "../alt/PixelCursor";
import { HUES, avatarHue, type Hue } from "../alt/hues";
import { EXPORT_BODY, EXPORT_META, EXPORT_SHADOW, EXPORT_TITLE } from "./exportUi";
/**
 * THE SKILLS LIBRARY, AS A SHELF — AN EXPORT-ONLY ASSET.
 *
 * WHY THIS AND NOT THE SCREEN. `SkillsScreen` is the real product view: four
 * rows in app chrome, sized for someone scanning a list they already
 * understand. As a downloadable asset it under-sells the idea — a list of four
 * rows says "there are four skills", when the claim is that a company builds a
 * LIBRARY of them and every agent draws on it.
 *
 * So this borrows the template wall's treatment, for the same reason it works
 * there: an oversized grid on a ruled board, dissolved into the ground by a
 * single radial mask rather than terminated by a frame. Nothing stops the
 * artwork except light falling off, so the eye assumes the shelf continues
 * past what is shown. One card sits under the light at full weight with the
 * pointer resting on it, which gives a dense grid one thing to actually look
 * at — and names WHICH skill the frame is about.
 *
 * A skill is somebody's expertise written down once, so the author is the
 * credential and the use count is the proof it gets reused. Both are on every
 * card; neither is on the marketing copy.
 *
 * Static by design, like every other frame on the export page.
 */

type Skill = {
  name: string;
  captures: string;
  icon: BoxIcon;
  group: string;
  by: string;
  initials: string;
  /* Colour belongs to the GROUP, not to the card's position in the shelf, so
   the same part of the company reads the same way in every asset. */
  hue: Hue;
  used: number;
};

/** Named as the piece of knowledge itself, not as a sentence about it — a
 * library of nouns scans, a list of sentences has to be read. Matches the
 * product screen's own set so the two assets agree. */
const SKILLS: Skill[] = [{
  name: 'Inbound lead qualification',
  captures: 'The questions we ask, the disqualifiers, and where the answer gets written.',
  icon: Filter,
  group: 'Marketing',
  by: 'Priya Nair',
  initials: 'PN',
  hue: HUES.clay,
  used: 142
}, {
  name: 'Renewal email drafting',
  captures: 'Tone, structure and the account facts every renewal note has to carry.',
  icon: Mail,
  group: 'Sales',
  by: 'Marcus Lee',
  initials: 'ML',
  hue: HUES.teal,
  used: 211
}, {
  name: 'Weekly reporting format',
  captures: 'Which numbers lead, what gets compared, and how the summary is written.',
  icon: BarChart3,
  group: 'Leadership',
  by: 'Sarah Chen',
  initials: 'SC',
  hue: HUES.indigo,
  used: 96
}, {
  name: 'P1 incident escalation',
  captures: 'Who is paged, in what order, and what the first customer update says.',
  icon: Siren,
  group: 'Customer Success',
  by: 'Tom Alvarez',
  initials: 'TA',
  hue: HUES.plum,
  used: 38
},
// The shelf shows one more card than fits so the row runs off the right edge.
// These two exist to be cropped — the library has to look longer than the
// frame for the fade to mean anything.
{
  name: 'Contract redline review',
  captures: 'The clauses we never accept, and the fallback language for each one.',
  icon: FileText,
  group: 'Legal',
  by: 'Dana Okafor',
  initials: 'DO',
  hue: HUES.sky,
  used: 64
}, {
  name: 'Candidate debrief notes',
  captures: 'What each interviewer covers, and how a recommendation is written up.',
  icon: Users,
  group: 'People',
  by: 'Jules Weber',
  initials: 'JW',
  hue: HUES.moss,
  used: 51
}, {
  name: 'Discount approval limits',
  captures: 'What each tier can approve alone, and when it has to go to finance.',
  icon: BadgePercent,
  group: 'Finance',
  by: 'Ana Duarte',
  initials: 'AD',
  hue: HUES.amber,
  used: 87
}, {
  name: 'Churn save playbook',
  captures: 'The signals we act on, who reaches out, and what is offered at each step.',
  icon: LifeBuoy,
  group: 'Customer Success',
  by: 'Ken Ibarra',
  initials: 'KI',
  hue: HUES.plum,
  used: 119
}];

/**
 * THE SHELF RUNS OFF BOTH EDGES.
 *
 * The first version dimmed the un-lit cards to 72% and dropped a radial
 * vignette over everything, which made the library look tentative — three
 * greyed options and one selected, as if you were being asked to choose. The
 * claim is the opposite: a company accumulates dozens of these and every one
 * is live.
 *
 * So nothing is dimmed. Every card is at full strength, the row is WIDER than
 * the frame, and a horizontal fade cuts it at both sides — the same device a
 * marquee uses to say "this continues". What you see is a window onto a longer
 * shelf, not the whole of a small one.
 */
const EDGE_FADE = 'linear-gradient(to right, transparent 0%, #000 18%, #000 82%, transparent 100%)';
function SkillCard({
  skill,
  className = ''
}: {
  skill: Skill;
  className?: string;
}) {
  const Icon = skill.icon;
  const hue = skill.hue;
  return <div className={`rounded-[16px] border border-[#EFE7DA] bg-white p-4 ${className}`} data-id="element-3362" data-mp="2v44cf9e0zh7o">
      <div className="flex items-center gap-2.5" data-id="element-3363" data-mp="2q6c9lkbueaop">
        {/* A tinted tile with a saturated glyph, matching the product screen.
         Ink tiles across a whole shelf gave every card the same dark anchor,
         so nothing distinguished one part of the company from another. */}
        <span aria-hidden="true" className="flex size-8 shrink-0 items-center justify-center rounded-xl" style={{
        backgroundColor: hue.tint,
        boxShadow: `inset 0 0 0 1px ${hue.solid}22`
      }} data-id="element-3364" data-mp="2cefmxvsy0szp">
          <Icon size={14} strokeWidth={2} style={{
          color: hue.ink
        }} data-id="element-3365" data-mp="2m3z7r707uwx7" />
        </span>
        {/* The group is what the colour encodes, so it is set in that colour
         rather than in the same muted grey on every card. */}
        <span className="truncate text-[12px] font-semibold" style={{
        color: hue.ink
      }} data-id="element-3366" data-mp="1tmddp5imsezh">
          {skill.group}
        </span>
      </div>

      <p className={`${EXPORT_TITLE} mt-3`} data-id="element-3367" data-mp="2ed8we2b1whfe">{skill.name}</p>
      <p className={`${EXPORT_BODY} mt-1.5`} data-id="element-3368" data-mp="2eawefp5am72c">{skill.captures}</p>

      {/* Authorship and reuse, on one line. The person is the credential; the
       count is the evidence it was worth writing down. */}
      <div className="mt-4 flex items-center gap-2 border-t border-[#F1EAE0] pt-3" data-id="element-3369" data-mp="2n7dp6r9qw2xk">
        <span aria-hidden="true" className="flex size-[18px] shrink-0 items-center justify-center rounded-full text-[7.5px] font-bold text-white" style={{
        backgroundColor: avatarHue(skill.initials)
      }} data-id="element-3370" data-mp="2s3m0ftktnbzg">
          {skill.initials}
        </span>
        <span className={`${EXPORT_META} truncate font-semibold text-[#2B211A]`} data-id="element-3371" data-mp="24ejlwulhhz96">{skill.by}</span>
        <span className="ml-auto shrink-0 rounded-full bg-[#F1EAE0] px-2 py-0.5 text-[11.5px] font-bold text-[#6B5D52]" data-id="element-3372" data-mp="29e5zj5585hii">
          {skill.used} uses
        </span>
      </div>
    </div>;
}
/**
 * FALLOFF FROM THE CENTRE.
 *
 * One card is the subject; the rest recede from it symmetrically. Three things
 * fall off together — scale, opacity and lift — because any one of them alone
 * reads as a mistake: scale alone looks like a broken grid, opacity alone
 * looks like disabled cards, and lift alone is invisible. Together they read
 * as depth, which is what says "there are more of these behind".
 */
const STEPS = [{
  scale: 1,
  opacity: 1,
  y: 0
}, {
  scale: 0.9,
  opacity: 0.72,
  y: 10
}, {
  scale: 0.82,
  opacity: 0.4,
  y: 18
}];
export function SkillShelf({
  /**
   * How many cards flank the centre one on EACH side. Two on a landscape
   * plate (five in view), one on a vertical plate (three) — the phone frame
   * cannot hold five without the outer pair becoming slivers.
   */
  spread = 2
}: {
  spread?: 1 | 2;
} = {}) {
  const count = spread * 2 + 1;
  const visible = SKILLS.slice(0, count);
  /*
   * A FIXED CARD WIDTH, NOT A PERCENTAGE.
   *
   * Percentage widths divided the frame between however many cards were in
   * view, so adding a card shrank the type — the outer pair ended up unreadable
   * at export size, which is the one thing these plates cannot be. A fixed
   * width means the card is the same object whatever the plate does; the row
   * simply overflows and the fade cuts it, which is the effect anyway.
   *
   * 236px is close to square once the description wraps to three lines. Wider
   * cards read as list rows, and a row of them is a table.
   */
  const CARD = 236;
  return <div aria-hidden="true" className="relative w-full select-none overflow-hidden" style={{
    maskImage: EDGE_FADE,
    WebkitMaskImage: EDGE_FADE
  }} data-id="element-3373" data-mp="vyjt3lz1vxax">
      {/* No ruled board. Against cards that already recede, the grid was a
       second thing competing for the eye and it made a clean composition look
       like a screenshot of a canvas tool. The falloff alone says the library
       continues. */}
      <div className="relative flex items-center justify-center gap-4 py-10" data-id="element-3374" data-mp="2he9061hzzggz">
        {visible.map((skill, index) => {
        const distance = Math.abs(index - spread);
        const step = STEPS[distance];
        const centre = distance === 0;
        return <div key={skill.name} className="relative flex-none" style={{
          width: CARD,
          // Scaled about its own centre, so the row stays on one axis and
          // the recession reads as distance rather than as misalignment.
          transform: `translateY(${step.y}px) scale(${step.scale})`,
          opacity: step.opacity,
          // The subject sits in front of the cards it overlaps.
          zIndex: 10 - distance
        }} data-id="element-3375" data-mp="5qx6z4gvxsh5" data-mpi={index}>
              <SkillCard skill={skill} className={centre ? EXPORT_SHADOW : 'shadow-[0_1px_2px_rgba(43,33,26,0.05),0_12px_28px_-18px_rgba(43,33,26,0.18)]'} data-id="element-3376" data-mp="oiycuqs2uj02" />
              {/* Half on and half off the card, so it reads as resting over the
               library rather than as an icon inside one. */}
              {centre && <PixelCursor className="pointer-events-none absolute -bottom-6 -left-5 h-14 w-auto drop-shadow-[0_8px_14px_rgba(43,33,26,0.35)]" data-id="element-3377" data-mp="2z8oir5sg8cfd" />}
            </div>;
      })}
      </div>
    </div>;
}